import { useEffect, useMemo, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  useCampaignUiStore,
  useCampaignCapabilitiesStore,
  useMarkThreadRead,
  usePostRosterMessage,
  useEditRosterMessage,
  useDeleteRosterMessage,
  useRosterThread,
  campaignKeys,
} from '@discovr/features';
import { useCampaignPanelControls } from '../useCampaignPanelControls';
import {
  CreatorReviewPanel,
  type CreatorReviewDeliverable,
} from '../../components/common/CreatorReview';
import type { ReviewThreadEntry } from '../../lib/models';
import { useThreadsSSE } from '@discovr/common';
import { mapThreadMessages } from '../../components/common/mapThreadMessages';
import { isThreadLockedError } from '../../components/common/notificationErrors';
import { resolveThreadLocked } from '../../components/common/resolveThreadLocked';
import { isRejectedStatus } from './CreatorsTab/helpers';

function senderRoleToActor(role?: string): string {
  switch (role) {
    case 'admin':
    case 'admin_member':
    case 'super_admin':
      return 'admin';
    case 'brand_owner':
    case 'brand_admin':
    case 'brand_member':
      return 'brand';
    case 'creator':
      return 'creator';
    case 'talent_manager':
      return 'talent_manager';
    default:
      return role || 'legacy';
  }
}

type RawCreator = Record<string, unknown> & {
  platform?: { name?: string; username?: string; profile_picture_url?: string };
  creator?: { name?: string; email?: string };
  snapshot_metrics?: { followers?: number; avg_views?: number; engagement_rate?: number };
};

type CreatorView = {
  id: string;
  name: string;
  email: string;
  handle: string;
  followers: number;
  avgViews: number;
  engagementRate: number;
  avatarUrl: string;
  status: string;
  deliverables: CreatorReviewDeliverable[];
  totalCost: number;
};

function normalize(raw: RawCreator): CreatorView {
  const sm = raw?.snapshot_metrics;
  const creator: { name?: string; email?: string } = raw?.creator ?? {};
  const name =
    (creator.name as string | undefined) ||
    raw.platform?.name ||
    raw.platform?.username ||
    'Creator';
  const email = (creator.email as string | undefined) || '';
  const followers = sm?.followers ?? 0;
  const avgViews = sm?.avg_views ?? 0;
  const engRate = sm?.engagement_rate ?? 0;
  // platform is guaranteed non-null on V2 roster entries (RosterRowRef).
  const avatarUrl = raw.platform?.profile_picture_url || '';
  const handle = raw.platform?.username || '';
  const status = String(raw.status || 'pending').toLowerCase();
  const deliverables: CreatorReviewDeliverable[] = Array.isArray(raw.deliverables)
    ? raw.deliverables
    : [];
  const selectedTotal = deliverables
    .filter((d) => d.is_selected)
    .reduce((s, d) => s + Number(d.base_price ?? 0), 0);
  const totalCost = Number(raw.total_cost ?? raw.final_amount ?? selectedTotal) || 0;
  return {
    id: String(raw.id ?? ''),
    name,
    email,
    handle,
    followers,
    avgViews,
    engagementRate: engRate,
    avatarUrl,
    status,
    deliverables,
    totalCost,
  };
}

function statusTone(status: string): 'default' | 'pending' | 'approved' | 'rejected' | 'revision' {
  if (isRejected(status)) return 'rejected';
  if (status.includes('approved') || status.includes('accepted')) return 'approved';
  if (status.includes('pending')) return 'pending';
  return 'default';
}

// isRejectedStatus is the shared terminal-status predicate; 'rejected' is the
// post-level status this drawer also renders.
function isRejected(status: string): boolean {
  return isRejectedStatus(status) || status === 'rejected';
}

export function ReviewThreadDrawer() {
  const { permissions, viewerIsSuperAdmin } = useCampaignCapabilitiesStore();
  // Read-only members may open the conversation and read history, but cannot
  // post. The composer renders disabled (not hidden) with a tooltip.
  const accessLevel = useCampaignCapabilitiesStore((s) => s.accessLevel);
  const isReadOnly = accessLevel === 'read';
  // Header Approve / Reject reuse the same handlers the Creators tab fires from
  // its approval dialog (shared capabilities store).
  const approveCreatorAsBrand = useCampaignCapabilitiesStore((s) => s.approveCreatorAsBrand);
  const rejectCreatorApproval = useCampaignCapabilitiesStore((s) => s.rejectCreatorApproval);
  const [reviewBusy, setReviewBusy] = useState(false);
  const { convCreator, campaignId } = useCampaignUiStore();
  const { closePanels } = useCampaignPanelControls();
  const queryClient = useQueryClient();

  // Selection thread fetch via the unified
  // /api/v1/roster/{entry_id}/threads/selection/messages endpoint.
  // entryId empty disables the query (panel closed). Stale-time 0 +
  // post-message invalidation keeps the cache fresh; SSE
  // EventUnreadCount + EventNewMessage land on the same key from
  // useThreadsSSE.
  const entryId = convCreator?.id ?? '';
  const threadQuery = useRosterThread(entryId, 'selection');
  // Pass campaignId so a sent message also re-sorts the roster (recency) for the
  // sender, who is excluded from the new_message SSE.
  const postMessage = usePostRosterMessage(campaignId ?? undefined);
  const editMessage = useEditRosterMessage(campaignId ?? undefined);
  const deleteMessage = useDeleteRosterMessage(campaignId ?? undefined);
  const markThreadRead = useMarkThreadRead(campaignId ?? undefined);
  const [draft, setDraft] = useState('');

  // Clear unread + bump BE cursor on dialog open. Fires once per open
  // (entryId flip) — the mutate handle is stable so no ref dance.
  useEffect(() => {
    if (!entryId) return;
    markThreadRead.mutate({ entryId, type: 'selection' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entryId]);

  // Re-mark read when a new message arrives while this drawer is open.
  // useThreadsSSE adds a listener to the existing SSEManager singleton —
  // it does NOT open a second HTTP connection.
  useThreadsSSE({
    enabled: !!entryId,
    onNewMessage: (payload) => {
      // Only react when the message is for the currently-open entry.
      if (payload.entry_id !== entryId) return;
      markThreadRead.mutate({ entryId, type: 'selection' });
    },
  });

  // Reset draft when the dialog opens for a different entry — don't
  // leak typed text across rows. Adjust-during-render (guarded by an
  // entryId transition check) instead of an effect, so the cleared draft
  // is visible on the very first render for the new entry.
  const [prevEntryId, setPrevEntryId] = useState(entryId);
  // Locally flips the composer to the locked state on a 423 send failure —
  // see onAddComment below. selection_thread_locked on convCreator is
  // fetch-time and won't reflect a lock that happened while this drawer was
  // already open. Reset below alongside the draft whenever the drawer opens
  // for a (possibly different) entry.
  const [threadLockedLive, setThreadLockedLive] = useState(false);
  if (entryId !== prevEntryId) {
    setPrevEntryId(entryId);
    setDraft('');
    setThreadLockedLive(false);
  }

  // convCreator is the full RosterRowRef — use it directly rather than
  // searching through a separate creators list.
  const rawMatch = (convCreator ?? null) as RawCreator | null;

  const c = useMemo(() => (rawMatch ? normalize(rawMatch) : null), [rawMatch]);

  const deliverables = useMemo<CreatorReviewDeliverable[]>(() => {
    if (!c) return [];
    return c.deliverables;
  }, [c]);

  const totalCost = useMemo<number>(() => {
    if (!c) return 0;
    return c.totalCost ?? 0;
  }, [c]);

  // Shared projection; this surface is the only one that remaps the raw BE
  // role through senderRoleToActor.
  const threadEntries = useMemo<ReviewThreadEntry[]>(
    () => mapThreadMessages(threadQuery.data, senderRoleToActor),
    [threadQuery.data],
  );

  if (!convCreator || !c) return null;

  // Locked threads (post-rejection, accepted_by_admin, anywhere past the
  // pending|accepted_by_brand window) remain VIEWABLE — read-only history
  // is useful for the reviewer to see what was said before the lock. The
  // composer below is gated on `canAddComment = !threadLocked` so only
  // sends are blocked. Previously this branch closed the panel on every
  // rejected state, which made the chat icon click feel broken.
  //
  // rowThreadLocked is the fetch-time hint (roster row status/field),
  // available before threadQuery resolves. Once GetMessagesResponse.locked
  // has loaded it is the BE's authoritative answer and supersedes the row
  // hint — see resolveThreadLocked's own doc comment. threadLockedLive (the
  // 423-on-send backstop below) stays a separate override applied only to
  // canAddComment, not folded into `threadLocked` itself.
  const rowThreadLocked =
    isRejected(c.status) ||
    !!(convCreator as Record<string, unknown> | null)?.selection_thread_locked;
  const threadLocked = resolveThreadLocked(threadQuery.data?.locked, rowThreadLocked, false);
  // Same signals plus the 423-on-send backstop. Single source of truth for the
  // composer gate AND the Edit/Delete gate, so the two cannot drift apart.
  const threadLockedLiveIncluded = resolveThreadLocked(
    threadQuery.data?.locked,
    rowThreadLocked,
    threadLockedLive,
  );

  const close = () => closePanels();

  // Approve / Reject are offered only in the brand-approvable window (pending),
  // gated on the shared capability + presence of the handlers. Approve submits
  // the deliverables already marked selected on the entry; reject fires with an
  // empty reason (no reason prompt in this quick-action surface).
  const selectedDeliverables = c.deliverables
    .filter((d) => d.is_selected)
    .map((d) => String(d.type ?? ''))
    .filter(Boolean);
  const canReview =
    !threadLocked &&
    c.status === 'pending' &&
    !!permissions?.canApproveRejectCreators &&
    !!(approveCreatorAsBrand || rejectCreatorApproval);
  const handleApprove = async () => {
    if (!approveCreatorAsBrand || selectedDeliverables.length === 0) return;
    setReviewBusy(true);
    try {
      await approveCreatorAsBrand(convCreator as Record<string, unknown>, selectedDeliverables);
      close();
    } finally {
      setReviewBusy(false);
    }
  };
  const handleReject = async () => {
    if (!rejectCreatorApproval) return;
    setReviewBusy(true);
    try {
      await rejectCreatorApproval(convCreator as Record<string, unknown>, '');
      close();
    } finally {
      setReviewBusy(false);
    }
  };

  return (
    <CreatorReviewPanel
      open
      onClose={close}
      creatorName={c.name}
      creatorAvatarUrl={c.avatarUrl || undefined}
      creatorEmail={c.email || undefined}
      creatorHandle={c.handle || undefined}
      shareCreatorUsername={c.handle || undefined}
      followers={c.followers}
      avgViews={c.avgViews}
      engagementRate={c.engagementRate}
      statusLabel={c.status.replace(/_/g, ' ')}
      statusTone={statusTone(c.status)}
      deliverables={deliverables}
      totalCost={totalCost}
      thread={threadEntries}
      threadDefaultOpen
      threadLoading={threadQuery.isLoading}
      threadError={threadQuery.isError}
      onThreadRetry={() => void threadQuery.refetch()}
      canAddComment={!threadLockedLiveIncluded}
      locked={threadLockedLiveIncluded}
      readOnly={isReadOnly}
      addCommentBusy={postMessage.isPending}
      onAddComment={async (body, replyTo) => {
        // Defense in depth: read-only members never post even if the
        // composer is somehow triggered.
        if (isReadOnly || !entryId) return;
        try {
          await postMessage.mutateAsync({ entryId, type: 'selection', body, replyTo });
        } catch (err) {
          // BE returns 423 when the thread locked while this drawer was
          // already open — selection_thread_locked on convCreator is
          // fetch-time and won't reflect a lock that happened mid-session.
          // Flip the composer to locked immediately rather than waiting on
          // a refetch. Re-throw either way — ChatThread's own catch rolls
          // back the optimistic bubble + restores the draft on any failed
          // send, which still applies here since the message never sent.
          if (isThreadLockedError(err)) {
            setThreadLockedLive(true);
            toast.error('This conversation is locked');
            if (campaignId) {
              void queryClient.invalidateQueries({ queryKey: campaignKeys.rosterAll(campaignId) });
            }
          }
          throw err;
        }
      }}
      onEditMessage={async (messageId, body) => {
        if (isReadOnly || !entryId) return;
        try {
          await editMessage.mutateAsync({ entryId, type: 'selection', messageId, body });
        } catch (err) {
          if (isThreadLockedError(err)) {
            setThreadLockedLive(true);
            toast.error('This conversation is locked');
          } else {
            toast.error('Could not edit the message');
          }
          throw err;
        }
      }}
      onDeleteMessage={async (messageId) => {
        if (isReadOnly || !entryId) return;
        try {
          await deleteMessage.mutateAsync({ entryId, type: 'selection', messageId });
        } catch (err) {
          if (isThreadLockedError(err)) {
            setThreadLockedLive(true);
            toast.error('This conversation is locked');
          } else {
            toast.error('Could not delete the message');
          }
          throw err;
        }
      }}
      viewerIsSuperAdmin={viewerIsSuperAdmin}
      draft={draft}
      setDraft={setDraft}
      viewerActor={permissions?.canSendBrief ? 'admin' : 'brand'}
      canReview={canReview}
      approveDisabled={selectedDeliverables.length === 0}
      reviewBusy={reviewBusy}
      onApprove={approveCreatorAsBrand ? handleApprove : undefined}
      onReject={rejectCreatorApproval ? handleReject : undefined}
    />
  );
}

export default ReviewThreadDrawer;
